import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createFmeReturnship } from "api/fmeReturnship";
import { parseMgmtError } from "api/mgmt";
import FamiShipQr from "./FamiShipQr";

/**
 * 全家歸還完成畫面 — 向日翊建退貨通訂單取「寄件編號」,產成 QRCode,
 * 並引導顧客帶包裹到全家門市 FamiPort 機台過刷、交件。
 *
 * QR 與畫面顯示的寄件編號都是 11 碼 OrderNo(日翊機台規格)。
 * 全家窗口 2026-07-24 確認:檢核碼不需要,機台掃描/手動輸入都收 11 碼。
 *
 * 掛在歸還完成畫面(fami_tage)。掛載即建單;失敗提供重試,不影響歸還本身已完成的事實。
 */
interface Props {
  epkId?: string;
  rentalLogId?: number;
  senderName?: string;
  senderPhone?: string;
  /** 這次是重掃同一顆包裝,顯示的是稍早那組編號 */
  isRescan?: boolean;
}

const findBtn: React.CSSProperties = {
  marginTop: 16,
  display: "inline-block",
  padding: "10px 22px",
  background: "#008a45",
  color: "#fff",
  fontSize: 15,
  fontWeight: 600,
  borderRadius: 24,
  border: "none",
  cursor: "pointer",
};

const FamiReturnQrc: React.FC<Props> = ({ epkId, rentalLogId, senderName, senderPhone, isRescan }) => {
  const navigate = useNavigate();
  const [orderNo, setOrderNo] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [flowLabel, setFlowLabel] = useState<string>("");
  // 日翊慢時(實測 25 秒)effect 重跑或重按會再打一次,同一筆歸還開出兩張單;要號中就不再送
  const inFlight = useRef<boolean>(false);

  const fetchQrc = useCallback(async () => {
    if (!epkId) {
      setError("缺少包裝編號,無法產生寄件條碼");
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setError("");
    try {
      const data = await createFmeReturnship({
        epk_id: epkId,
        rental_log_id: rentalLogId, // PG-186 連歸還單:後端據此判斷 B02 交件確認後才可領券
        sender_name: senderName,
        sender_phone: senderPhone,
      });
      setOrderNo(data.order_no);
      setExpiresAt(data.expires_at ?? null);
      setFlowLabel(data.flow_label ?? "");
    } catch (e: any) {
      setError(parseMgmtError(e));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [epkId, rentalLogId, senderName, senderPhone]);

  useEffect(() => {
    fetchQrc();
  }, [fetchQrc]);

  if (loading) {
    return (
      <p style={{ textAlign: "center" }}>
        寄件條碼產生中…
        <br />
        <span style={{ fontSize: 12, color: "#888" }}>可能需要 10 到 30 秒,請不要離開此畫面</span>
      </p>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center" }}>
        <p style={{ color: "#c0392b" }}>{error}</p>
        <button type="button" onClick={fetchQrc} style={{ marginTop: 8 }}>
          重新產生寄件條碼
        </button>
      </div>
    );
  }

  if (!orderNo) {
    return null;
  }

  return (
    <div style={{ width: "100%", maxWidth: 360, margin: "0 auto", textAlign: "center" }}>
      {isRescan && flowLabel !== "consigned" && (
        // 重掃不會產生新編號,不講清楚使用者會以為又還了一次
        <p
          style={{
            margin: "0 auto 10px",
            padding: "8px 12px",
            background: "#fff6e5",
            border: "1px solid #f0ad4e",
            borderRadius: 8,
            color: "#7a4e00",
            fontSize: 13,
            lineHeight: 1.6,
            maxWidth: 320,
          }}
        >
          這個包裝您稍早已經歸還過了,以下是同一組寄件編號,直接拿去全家交件即可。
        </p>
      )}
      {flowLabel === "consigned" && (
        <p
          style={{
            margin: "0 auto 10px",
            padding: "8px 12px",
            background: "#e3f2ea",
            border: "1px solid #008a45",
            borderRadius: 8,
            color: "#00622f",
            fontSize: 13,
            lineHeight: 1.6,
            maxWidth: 320,
          }}
        >
          這個包裝已完成交件,不需要再跑一趟。優惠券約 2 到 3 個工作天後可於配客嘉 LINE 領取。
        </p>
      )}
      <FamiShipQr orderNo={orderNo} expiresAt={expiresAt} />
      <p style={{ marginTop: 10, fontSize: 12.5, color: "#555", lineHeight: 1.6 }}>
        離開這個畫面後,寄件編號可在「會員中心 → 歸還紀錄」再次查看,也會傳到您的 LINE。
      </p>

      {/* 暫時隱藏「找最近的全家」— 歸還地圖目前沒有全家點位、不一定需要。
          地圖有點位後把 false 拿掉即可恢復(view-only + resume 機制已備妥)。 */}
      {false && (
        <button
          type="button"
          style={findBtn}
          onClick={() =>
            // 同分頁進我們的歸還地圖(/nearby 需 LIFF,另開新分頁會跳出 LIFF 壞掉)。
            // ?view=1 = 純看點位(藏「開始歸還」);地圖「返回歸還」會用 ?resume=1 回到這張寄件 QRC,不重跑流程。
            navigate("/nearby?view=1")
          }
        >
          找最近的全家
        </button>
      )}
    </div>
  );
};

export default FamiReturnQrc;
